import { aiMimeTypes } from '../../constants';

/**
 * One source of truth for how a markdown code fence is recognised.
 *
 * Both the client's `remarkCodeBlock` plugin and the server's finalize checks
 * need the same answer to "is this line a fence, and what language is it?".
 * When those two disagree, the server can decide a turn said nothing while the
 * user is looking at text, which is the exact failure this module guards.
 *
 * The rules follow CommonMark, which matters because reasoning fences carry
 * model output that routinely contains code blocks of its own:
 *
 * - Up to three leading spaces, then three or more backticks or tildes. Four or
 *   more spaces is an indented code block, not a fence, and a leading tab counts
 *   as four. The bound also keeps the pattern linear: an unbounded leading
 *   whitespace class backtracks quadratically on a long run of whitespace that
 *   never reaches a fence character, and `displayText` is model output.
 * - A closer must use the same character, be at least as long as the opener,
 *   and carry no info string. A line like ` ```ts ` inside a thinking block is
 *   therefore content, not the end of that block.
 */
const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})(.*)$/;

export interface FenceMarker {
  readonly character: string;
  /** A closer carries nothing after the run of fence characters. */
  readonly hasInfoString: boolean;
  readonly language: string;
  readonly length: number;
}

export function parseFenceMarker(line: string): FenceMarker | null {
  const match = FENCE_LINE.exec(line);
  if (!match) return null;

  const run = match[1] ?? '';
  const info = (match[2] ?? '').trim();
  /*
   * An info string may not contain the fence character itself, so a line of
   * backticks with trailing backticks is not a valid opener.
   */
  if (info.includes(run[0] ?? '')) return null;

  return {
    character: run[0] ?? '`',
    hasInfoString: info.length > 0,
    language: info.split(/\s+/)[0]?.toLowerCase() ?? '',
    length: run.length,
  };
}

/**
 * Whether `marker` ends the block opened by `opener`. Anything else on a line
 * inside an open fence is content, however fence-like it looks.
 */
export function getIsFenceCloser(marker: FenceMarker, opener: FenceMarker): boolean {
  if (marker.hasInfoString) return false;
  if (marker.character !== opener.character) return false;
  return marker.length >= opener.length;
}

/**
 * The fence still open at the end of `text`, or `null` when every block closed.
 * Callers append a matching closer so text added afterwards is not swallowed as
 * fence content.
 */
export function getUnclosedFence(text: string): FenceMarker | null {
  let opener: FenceMarker | null = null;

  for (const line of text.split('\n')) {
    const marker = parseFenceMarker(line);
    if (!marker) continue;
    if (opener === null) {
      opener = marker;
      continue;
    }
    if (getIsFenceCloser(marker, opener)) opener = null;
  }

  return opener;
}

/** The closer that ends `marker`, e.g. ```` ``` ```` or `~~~~`. */
export function getFenceCloserFor(marker: FenceMarker): string {
  return marker.character.repeat(marker.length);
}

/**
 * Fence languages whose contents are machine output that the client collapses
 * into a card: reasoning, tool calls, redline notes, artifact payloads.
 *
 * Everything else is text the user reads. `plain-text` is deliberately excluded
 * because the serializer renders its body verbatim, and an unlabelled or
 * language-tagged block (```` ```ts ````) is a code sample that can legitimately
 * be the whole answer.
 */
const MACHINE_OUTPUT_FENCE_LANGUAGES: ReadonlySet<string> = new Set(
  Object.values(aiMimeTypes).filter(mimeType => mimeType !== aiMimeTypes.plainText)
);

export function getIsMachineOutputFence(language: string): boolean {
  return MACHINE_OUTPUT_FENCE_LANGUAGES.has(language);
}

/**
 * How many fence marker lines a block of text contains, without pairing them.
 *
 * `remarkCodeBlock` uses this on a slice remark has already identified as one
 * code node, where a single marker means the block never closed. Prefer
 * `getUnclosedFence` anywhere the opener and closer have to actually match.
 */
export function countFenceMarkers(text: string): number {
  return text.split('\n').filter(line => parseFenceMarker(line) !== null).length;
}
